import React, { useState } from "react";
import styled from "styled-components";
import {
  colors2,
  TextInput,
  UI_BODY_SM_DF,
  UI_CAPTION,
  LoaderOverlay,
} from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
} from "./BaseIntegrationInterface";
import { useFetchPreflightData } from "../../hooks/useFetchPreflightData";

// ============================================
// ICON
// ============================================

export const SmartCompanyImportIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="6" fill="#10B981" />
    <path
      d="M16 7l3.5 5.5h-2v6h-3v-6h-2L16 7z"
      fill="#fff"
    />
    <circle cx="11" cy="22" r="2" fill="#fff" />
    <circle cx="16" cy="22" r="2" fill="#fff" />
    <circle cx="21" cy="22" r="2" fill="#fff" />
  </svg>
);

// ============================================
// STYLED
// ============================================

// Scrollable wrapper around the shared InterfaceContainer. The base container
// (used by other integrations) has no overflow rules and gets clipped by the
// outer modal. Smart Company Import has a two-step flow (company input +
// profile preview) so we cap height and let the user scroll.
const ScrollableInterfaceContainer = styled(InterfaceContainer)`
  max-height: 75vh;
  overflow-y: auto;
  /* Reserve a little room for the absolutely-positioned LoaderOverlay so it
     doesn't get cut off mid-scroll */
  position: relative;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const StepDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.$active ? colors2.blue : colors2.grey20)};
`;

const StepLabel = styled(UI_CAPTION)`
  color: ${(p) => (p.$active ? colors2.grey80 : colors2.grey40)};
`;

const FieldRow = styled.div`
  margin-top: 16px;
`;

const ProfileBlock = styled.div`
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
`;

const ProfileLabel = styled(UI_CAPTION)`
  color: ${colors2.grey60};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const ErrorMessage = styled(UI_CAPTION)`
  color: ${colors2.red};
  margin-top: 8px;
`;

const InfoText = styled(UI_BODY_SM_DF)`
  color: ${colors2.grey60};
  margin-top: 8px;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  margin: 2px 4px 2px 0;
  font-size: 12px;
  border-radius: 12px;
  background: ${colors2.grey5};
  color: ${colors2.grey80};
`;

// ============================================
// SMART COMPANY IMPORT INTERFACE
// ============================================

export const SmartCompanyImportInterface = ({ adapterConfig, onSubmit, isLoading }) => {
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [optionalDescription, setOptionalDescription] = useState("");
  const [showOptionalDescription, setShowOptionalDescription] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [error, setError] = useState(null);

  const { mutate: fetchPreflight, isLoading: isPreflightLoading } = useFetchPreflightData();

  const currentStep = preflight ? 2 : 1;

  const handleResearch = () => {
    if (!companyName.trim() || !website.trim()) return;
    setError(null);
    fetchPreflight(
      {
        sourceId: adapterConfig.id,
        apiKey: "__platform_managed__",
        options: {
          companyName: companyName.trim(),
          website: website.trim(),
          ...(optionalDescription.trim() && { optionalUserDescription: optionalDescription.trim() }),
        },
      },
      {
        onSuccess: (data) => {
          setPreflight(data.preflightData || {});
        },
        onError: (err) => {
          setError(err.message || "Failed to research company. Try again or paste a description.");
        },
      }
    );
  };

  const handleBack = () => {
    setPreflight(null);
    setError(null);
  };

  const handleSubmit = () => {
    onSubmit({
      apiKey: "__platform_managed__",
      adapterOptions: {
        companyName: companyName.trim(),
        website: website.trim(),
        // Profile from preflight, passed through verbatim. Deep research runs
        // its own planner over this profile.
        confirmedProfile: preflight?.profile || {},
        deepResearchEnabled: true,
      },
    });
  };

  return (
    <ScrollableInterfaceContainer>
      {isPreflightLoading && <LoaderOverlay loaderText="Researching your company..." />}

      <InterfaceHeader>{adapterConfig.name}</InterfaceHeader>
      <InterfaceDescription>{adapterConfig.description}</InterfaceDescription>

      <StepRow>
        <StepDot $active={currentStep >= 1} />
        <StepLabel $active={currentStep === 1}>Tell us about your company</StepLabel>
        <StepDot $active={currentStep >= 2} />
        <StepLabel $active={currentStep === 2}>Confirm what we found</StepLabel>
      </StepRow>

      {currentStep === 1 && (
        <>
          <TextInput
            label="Company name"
            placeholder="e.g., Acme Corp"
            value={companyName}
            onChange={(_, v) => setCompanyName(v)}
          />
          <FieldRow>
            <TextInput
              label="Website"
              placeholder="e.g., acme.com or https://acme.com"
              value={website}
              onChange={(_, v) => setWebsite(v)}
            />
          </FieldRow>

          {!showOptionalDescription ? (
            <FieldRow>
              <HelpText>
                Want to skip the AI lookup?{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowOptionalDescription(true);
                  }}
                >
                  Describe yourself in one line instead
                </a>
              </HelpText>
            </FieldRow>
          ) : (
            <FieldRow>
              <TextInput
                label="One-line description (optional)"
                placeholder="What does your company do?"
                value={optionalDescription}
                onChange={(_, v) => setOptionalDescription(v)}
              />
            </FieldRow>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <InterfaceActions>
            <ButtonPrimary
              onClick={handleResearch}
              disabled={!companyName.trim() || !website.trim() || isPreflightLoading}
              isLoading={isPreflightLoading}
            >
              Research company
            </ButtonPrimary>
          </InterfaceActions>
        </>
      )}

      {currentStep === 2 && preflight && (
        <>
          <ProfileBlock>
            <ProfileLabel>Here's what we figured out</ProfileLabel>
            {preflight.profile?.summary && (
              <InfoText>{preflight.profile.summary}</InfoText>
            )}
            {preflight.profile?.industry && (
              <InfoText>
                Industry: <Pill>{preflight.profile.industry}</Pill>
              </InfoText>
            )}
            {preflight.profile?.target_audience && (
              <InfoText>Audience: {preflight.profile.target_audience}</InfoText>
            )}
            {Array.isArray(preflight.profile?.products) && preflight.profile.products.length > 0 && (
              <InfoText>
                Products:{" "}
                {preflight.profile.products.map((p, i) => (
                  <Pill key={i}>{p}</Pill>
                ))}
              </InfoText>
            )}
          </ProfileBlock>

          <HelpText>
            We'll run an AI research agent that Googles, reads pages, follows
            links, and iterates — like a human researcher — using the profile
            above. Takes 1–3 minutes per import.
          </HelpText>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <InterfaceActions>
            <ButtonSecondary onClick={handleBack} disabled={isLoading}>
              Back
            </ButtonSecondary>
            <ButtonPrimary
              onClick={handleSubmit}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Run deep research
            </ButtonPrimary>
          </InterfaceActions>
        </>
      )}
    </ScrollableInterfaceContainer>
  );
};

// ============================================
// META
// ============================================

export const smartCompanyImportIntegrationMeta = {
  id: "smartCompanyImport",
  name: "Smart Company Import (AI-curated)",
  description: "Just type your company name + website. We do the research and filter the noise.",
};

export default SmartCompanyImportInterface;
